import React, { useEffect, useState } from 'react';
import FeatherIcon from './FeatherIcon';
import { renderShareImage } from '../lib/shareImage';

export type SharePlatform = 'native' | 'x' | 'facebook' | 'linkedin' | 'download' | 'copy';

interface Props {
  /** The gift in cents, excluding any covered processing fee. Shown only when the donor opts in. */
  readonly amountCents?: number;
  /** Called on every share action, for analytics. */
  readonly onShare?: (platform: SharePlatform) => void;
}

const IMAGE_NAME = 'pawsitive-future-donation.jpg';

const formatUsd = (dollars: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars);

/** Shared links land on /donate, tagged so a gift that follows a share can be attributed to it. */
const shareLink = (platform: SharePlatform): string =>
  `${window.location.origin}/donate?utm_source=${platform}&utm_medium=social&utm_campaign=donor_share`;

const openShareWindow = (url: string): void => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

// The X mark — feather-icons only has the old bird.
const XLogo: React.FC = () => (
  <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const buttonClass =
  'inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border border-gray-200 bg-card text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors focus:ring-2 focus:ring-blue-500 focus:outline-none';

/**
 * @description Lets a donor post that they gave: the native share sheet (with an image, which is
 *   the only route onto Instagram), X, Facebook, LinkedIn, an image download and a copy button.
 *   Facebook and LinkedIn drop any prefilled text, so those buttons copy the caption first.
 */
const ShareDonation: React.FC<Props> = ({ amountCents, onShare }) => {
  const [includeAmount, setIncludeAmount] = useState(false);
  const [image, setImage] = useState<{ file: File; url: string } | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const amountText = includeAmount && amountCents ? formatUsd(amountCents / 100) : undefined;
  const caption =
    `I just donated${amountText ? ` ${amountText}` : ''} to Pawsitive Future Foundation to help families afford ` +
    'veterinary care for their pets. 🐾 Join me in supporting @pawsitivefuture. #PawsitiveFuture';
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  // Rendered ahead of the tap: the share sheet must open within the tap's user activation, which
  // font loading and canvas encoding can outlast on Safari.
  useEffect(() => {
    let cancelled = false;
    renderShareImage(amountText).then((blob) => {
      if (cancelled || !blob) return;
      setImage({ file: new File([blob], IMAGE_NAME, { type: 'image/jpeg' }), url: URL.createObjectURL(blob) });
    });
    return () => { cancelled = true; };
  }, [amountText]);

  // Each image's object URL lives until the next image replaces it, or the component unmounts.
  useEffect(() => () => { if (image) URL.revokeObjectURL(image.url); }, [image]);

  const copy = (text: string, message: string): void => {
    navigator.clipboard?.writeText(text).then(() => setNotice(message), () => setNotice(null));
  };

  const shareNative = (): void => {
    onShare?.('native');
    const data: ShareData = { title: 'Pawsitive Future Foundation', text: caption, url: shareLink('native') };
    if (image && navigator.canShare?.({ files: [image.file] })) data.files = [image.file];
    // Dismissing the sheet rejects with AbortError; there is nothing to report.
    navigator.share(data).catch(() => undefined);
  };

  const shareTo = (platform: 'x' | 'facebook' | 'linkedin'): void => {
    onShare?.(platform);
    const link = encodeURIComponent(shareLink(platform));
    if (platform === 'x') {
      openShareWindow(`https://x.com/intent/post?text=${encodeURIComponent(caption)}&url=${link}`);
      return;
    }
    copy(caption, 'Caption copied — paste it into your post.');
    openShareWindow(platform === 'facebook'
      ? `https://www.facebook.com/sharer/sharer.php?u=${link}`
      : `https://www.linkedin.com/sharing/share-offsite/?url=${link}`);
  };

  return (
    <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-blue-200">
      <h3 className="font-semibold text-gray-800 mb-1 text-center">Share that you gave</h3>
      <p className="text-sm text-gray-600 text-center mb-4">
        Posting about your gift helps more pet families find us.
      </p>

      <div className="flex flex-col sm:flex-row items-center gap-4 mb-4">
        {image && (
          <img
            src={image.url}
            alt="The image that will be shared"
            className="w-28 shrink-0 rounded-lg shadow-md"
          />
        )}
        <div className="min-w-0 text-left">
          <p className="text-sm text-gray-700 mb-2 break-words">{caption}</p>
          {amountCents ? (
            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={includeAmount}
                onChange={(e) => setIncludeAmount(e.target.checked)}
                className="h-4 w-4"
              />
              Include my gift amount
            </label>
          ) : null}
        </div>
      </div>

      {canNativeShare && (
        <button type="button" onClick={shareNative} className="w-full btn-primary inline-flex items-center justify-center gap-2 py-3 rounded-lg mb-2">
          <FeatherIcon name="share" size={18} />
          Share…
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <button type="button" onClick={() => shareTo('x')} className={buttonClass} aria-label="Post on X">
          <XLogo />
          X
        </button>
        <button type="button" onClick={() => shareTo('facebook')} className={buttonClass} aria-label="Share on Facebook">
          <FeatherIcon name="facebook" size={16} />
          Facebook
        </button>
        <button type="button" onClick={() => shareTo('linkedin')} className={buttonClass} aria-label="Share on LinkedIn">
          <FeatherIcon name="linkedin" size={16} />
          LinkedIn
        </button>
        {image && (
          <a href={image.url} download={IMAGE_NAME} onClick={() => onShare?.('download')} className={buttonClass}>
            <FeatherIcon name="download" size={16} />
            Image
          </a>
        )}
        <button
          type="button"
          onClick={() => {
            onShare?.('copy');
            copy(`${caption} ${shareLink('copy')}`, 'Caption and link copied.');
          }}
          className={buttonClass}
        >
          <FeatherIcon name="copy" size={16} />
          Copy
        </button>
      </div>

      <p className="text-xs text-gray-500 text-center mt-3">
        Instagram: {canNativeShare ? 'tap Share… and pick Instagram, or download' : 'download'} the image, post it from your phone, and tag @pawsitivefuture.
      </p>
      {notice && (
        <p className="text-xs text-green-700 text-center mt-2" role="status" aria-live="polite">{notice}</p>
      )}
    </div>
  );
};

export default ShareDonation;
